import React, { useEffect, useMemo, useRef, useState } from "react";
import ModalCloseBtn from "@/components/ModalCloseBtn";
import {
  MessageSquare, Send, Plus, Users as UsersIcon, X, Search, Trash2, Check, ChevronLeft, CheckCircle2
} from "lucide-react";
import { toast } from "sonner";
import Layout, { PageHeader } from "@/components/Layout";
import api from "@/lib/api";
import { useAuth, ROLE_LABELS, ROLE_COLORS } from "@/lib/auth.jsx";
import { getNav } from "@/pages/navConfigs";

const POLL_MS = 5000;

export default function ChatPage() {
  const { user, customRole } = useAuth();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [openNew, setOpenNew] = useState(false);
  const [search, setSearch] = useState("");
  const [showOnMobile, setShowOnMobile] = useState("threads"); // "threads" | "chat"
  const scrollRef = useRef(null);

  const loadThreads = async () => {
    try {
      const r = await api.get("/dm/threads");
      setThreads(r.data || []);
    } catch {}
  };

  const loadMessages = async (tid) => {
    try {
      const r = await api.get(`/dm/threads/${tid}/messages`);
      setMessages(r.data || []);
      api.post(`/dm/threads/${tid}/read`, {}).catch(() => {});
      setTimeout(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, 50);
    } catch {}
  };

  useEffect(() => {
    loadThreads();
    const id = setInterval(() => {
      loadThreads();
      if (active) loadMessages(active.thread_id);
    }, POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [active?.thread_id]);

  useEffect(() => {
    if (active) loadMessages(active.thread_id);
    // eslint-disable-next-line
  }, [active?.thread_id]);

  const send = async () => {
    if (!body.trim() || !active) return;
    const trimmed = body.trim();
    setBody("");
    try {
      const r = await api.post(`/dm/threads/${active.thread_id}/messages`, { body: trimmed });
      setMessages((m) => [...m, r.data]);
      setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }), 30);
      loadThreads();
    } catch (e) { toast.error("Send failed"); setBody(trimmed); }
  };

  const removeThread = async (t) => {
    if (!window.confirm(`Delete this chat? Messages cannot be recovered.`)) return;
    try {
      await api.delete(`/dm/threads/${t.thread_id}`);
      if (active?.thread_id === t.thread_id) { setActive(null); setMessages([]); }
      loadThreads();
      toast.success("Chat deleted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const otherMember = (t) => {
    if (t.is_group) return null;
    return (t.members || []).find((m) => m.user_id !== user.user_id);
  };

  const threadTitle = (t) => t.is_group ? t.name : (otherMember(t)?.name || t.name);

  const filtered = threads.filter((t) => !search || threadTitle(t).toLowerCase().includes(search.toLowerCase()));

  const totalUnread = threads.reduce((a, b) => a + (b.unread_count || 0), 0);

  return (
    <Layout navItems={getNav(user.role, customRole)}>
      <div className="h-[calc(100vh-4rem)] lg:h-screen flex flex-col" data-testid="chat-page">
        <div className="px-4 lg:px-8 pt-6 pb-3 border-b border-slate-100 bg-white">
          <PageHeader
            title="Chat"
            subtitle="1:1 and group conversations. Polled every 5s."
            icon={MessageSquare}
            accent="indigo"
            actions={
              <div className="flex items-center gap-2">
                {totalUnread > 0 && <span className="px-2 py-0.5 rounded-full bg-rose-500 text-white text-xs font-bold" data-testid="total-unread">{totalUnread}</span>}
                <button onClick={() => setOpenNew(true)} className="btn-primary" data-testid="new-chat-btn"><Plus className="w-4 h-4" /> New chat</button>
              </div>
            }
          />
        </div>

        <div className="flex-1 grid lg:grid-cols-[320px_1fr] min-h-0 bg-slate-50">
          {/* Threads list */}
          <aside className={`${showOnMobile === "threads" ? "block" : "hidden"} lg:block bg-white border-r border-slate-100 flex flex-col min-h-0`}>
            <div className="p-3 border-b border-slate-100">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input className="input-pill text-sm w-full" style={{ paddingLeft: "2.5rem" }} placeholder="Search chats…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="chat-search" />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto custom-scrollbar">
              {filtered.length === 0 ? (
                <div className="p-12 text-center text-slate-400 text-sm">
                  No chats yet. Click <b>New chat</b> to start.
                </div>
              ) : (
                filtered.map((t) => {
                  const om = otherMember(t);
                  const isActive = active?.thread_id === t.thread_id;
                  return (
                    <button key={t.thread_id} onClick={() => { setActive(t); setShowOnMobile("chat"); }}
                      className={`w-full text-left p-3 flex items-center gap-3 border-b border-slate-50 transition-colors ${isActive ? "bg-indigo-50" : "hover:bg-slate-50"}`}
                      data-testid={`thread-${t.thread_id}`}>
                      <div className={`relative w-11 h-11 rounded-2xl flex items-center justify-center text-white font-bold flex-shrink-0 overflow-hidden ${t.is_group ? "bg-gradient-to-br from-fuchsia-500 to-pink-500" : "bg-gradient-to-br from-indigo-500 to-blue-500"}`}>
                        {t.is_group ? <UsersIcon className="w-5 h-5" /> : (om?.picture ? <img src={om.picture} alt="" className="w-full h-full object-cover" /> : (om?.name?.charAt(0)?.toUpperCase() || "?"))}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-semibold text-sm truncate">{threadTitle(t)}</div>
                          {(t.unread_count || 0) > 0 && (
                            <span className="bg-rose-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center" data-testid={`unread-${t.thread_id}`}>{t.unread_count}</span>
                          )}
                        </div>
                        <div className="text-[11px] text-slate-500 truncate">{t.last_message_text || (t.is_group ? `Group · ${(t.member_ids||[]).length} members` : ROLE_LABELS[om?.role] || "—")}</div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </aside>

          {/* Message panel */}
          <main className={`${showOnMobile === "chat" ? "block" : "hidden"} lg:block flex flex-col min-h-0`}>
            {!active ? (
              <div className="flex-1 flex items-center justify-center p-12 text-center">
                <div>
                  <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <div className="font-display font-bold text-slate-400 text-lg">Select a chat to start messaging</div>
                  <p className="text-xs text-slate-400 mt-2">Or click <b>New chat</b> to start a fresh conversation.</p>
                </div>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="p-3 border-b border-slate-100 bg-white flex items-center gap-3" data-testid="chat-header">
                  <button onClick={() => setShowOnMobile("threads")} className="lg:hidden p-1 rounded-lg hover:bg-slate-100"><ChevronLeft className="w-5 h-5" /></button>
                  <div className={`w-10 h-10 rounded-2xl flex items-center justify-center text-white font-bold flex-shrink-0 overflow-hidden ${active.is_group ? "bg-gradient-to-br from-fuchsia-500 to-pink-500" : "bg-gradient-to-br from-indigo-500 to-blue-500"}`}>
                    {active.is_group ? <UsersIcon className="w-5 h-5" /> : (otherMember(active)?.picture ? <img src={otherMember(active).picture} alt="" className="w-full h-full object-cover" /> : (otherMember(active)?.name?.charAt(0)?.toUpperCase() || "?"))}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold truncate">{threadTitle(active)}</div>
                    <div className="text-[10px] text-slate-500 truncate">
                      {active.is_group ? (active.members || []).map((m) => m.name).join(", ") : (ROLE_LABELS[otherMember(active)?.role] || "Direct chat")}
                    </div>
                  </div>
                  {(active.created_by === user.user_id || user.role === "admin") && (
                    <button onClick={() => removeThread(active)} className="p-2 rounded-lg hover:bg-rose-50 text-rose-500" data-testid="chat-delete-btn"><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>

                {/* Messages */}
                <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-2 bg-slate-50">
                  {messages.length === 0 && <div className="text-center text-slate-400 text-sm py-12">No messages yet — say hello!</div>}
                  {messages.map((m) => {
                    const mine = m.sender_id === user.user_id;
                    return (
                      <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.message_id}`}>
                        <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${mine ? "bg-indigo-500 text-white rounded-br-md" : "bg-white text-slate-800 border border-slate-100 rounded-bl-md"}`}>
                          {!mine && active.is_group && <div className="text-[10px] font-bold opacity-70 mb-0.5">{m.sender_name}</div>}
                          <div className="text-sm whitespace-pre-wrap break-words">{m.body}</div>
                          <div className={`text-[9px] mt-0.5 ${mine ? "text-indigo-100" : "text-slate-400"}`}>{new Date(m.created_at).toLocaleString()}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Composer */}
                <div className="p-3 border-t border-slate-100 bg-white flex items-end gap-2">
                  <textarea
                    rows={1}
                    className="input-pill flex-1 resize-none max-h-32"
                    placeholder="Type a message…"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                    data-testid="chat-composer"
                  />
                  <button onClick={send} disabled={!body.trim()} className="btn-primary" data-testid="chat-send-btn"><Send className="w-4 h-4" /></button>
                </div>
              </>
            )}
          </main>
        </div>
      </div>

      {openNew && <NewChatModal onClose={() => setOpenNew(false)} onCreated={(t) => { setOpenNew(false); setActive(t); setShowOnMobile("chat"); loadThreads(); }} />}
    </Layout>
  );
}

function NewChatModal({ onClose, onCreated }) {
  const [contacts, setContacts] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [groupName, setGroupName] = useState("");
  const [isGroup, setIsGroup] = useState(false);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/dm/contacts").then((r) => setContacts(r.data || []));
  }, []);

  const toggle = (uid) => {
    setPicked((p) => {
      const n = new Set(p);
      if (n.has(uid)) n.delete(uid); else n.add(uid);
      // Auto-toggle group mode when more than one selected
      if (n.size > 1) setIsGroup(true);
      return n;
    });
  };

  const submit = async () => {
    if (picked.size === 0) return toast.error("Pick at least one contact");
    setBusy(true);
    try {
      const r = await api.post("/dm/threads", {
        member_ids: Array.from(picked),
        is_group: isGroup || picked.size > 1,
        name: isGroup ? groupName : "",
      });
      onCreated(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const filtered = contacts.filter((c) => !search || `${c.name} ${c.email}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-md max-h-[90vh] flex flex-col relative" data-testid="new-chat-modal">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-indigo-50 to-fuchsia-50">
          <h3 className="font-display font-black text-lg flex items-center gap-2"><MessageSquare className="w-5 h-5 text-indigo-600" /> {picked.size > 1 ? "New group" : "New chat"}</h3>
          <button onClick={onClose} className="p-2 hover:bg-white rounded-lg"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 border-b border-slate-100">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input className="input-pill text-sm w-full" style={{ paddingLeft: "2.5rem" }} placeholder="Search contacts…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="new-chat-search" />
          </div>
          {picked.size > 1 && (
            <input className="input-pill text-sm mt-2" placeholder="Group name (optional)" value={groupName} onChange={(e) => setGroupName(e.target.value)} data-testid="new-chat-group-name" />
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-slate-400 text-sm">
              {contacts.length === 0 ? "You have no contacts available based on your role." : "No matches."}
            </div>
          ) : filtered.map((c) => {
            const on = picked.has(c.user_id);
            return (
              <button key={c.user_id} onClick={() => toggle(c.user_id)} className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors ${on ? "bg-emerald-50 border border-emerald-200" : "hover:bg-slate-50 border border-transparent"}`} data-testid={`contact-${c.user_id}`}>
                <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-orange-400 to-amber-300 text-white font-bold flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {c.picture ? <img src={c.picture} alt="" className="w-full h-full object-cover" /> : c.name?.charAt(0)?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <div className="font-semibold text-sm truncate">{c.name}</div>
                  <div className="text-[10px] text-slate-400 truncate">{c.email}</div>
                </div>
                <span className={`role-pill ${ROLE_COLORS[c.role]}`}>{ROLE_LABELS[c.role]}</span>
                {on && <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />}
              </button>
            );
          })}
        </div>

        <div className="px-5 py-4 border-t border-slate-100 flex items-center justify-between">
          <div className="text-xs text-slate-500">{picked.size} selected{picked.size > 1 ? " · creating a group" : ""}</div>
          <button onClick={submit} disabled={busy || picked.size === 0} className="btn-primary" data-testid="new-chat-create"><Check className="w-4 h-4" /> {busy ? "Starting…" : "Start chat"}</button>
        </div>
      </div>
    </div>
  );
}
